import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { InputAdornment } from "@remo-co/ui-core/src/components/InputAdornment";
import { ListItem } from "@remo-co/ui-core/src/components/ListItem";
import { TextField } from "@remo-co/ui-core/src/components/TextField";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { ScrollableView } from "@remo-co/ui-core/src/components/ScrollableView";
import { useI18n } from "i18n";
import {
  selectCompanies,
  selectCompanyId,
  selectMyCompany,
} from "modules/company/redux/selectors";
import { selectAuth } from "modules/auth/redux/selectors";
import { useSelector } from "react-redux";
import { ChevronRight } from "@remo-co/ui-core/src/icons/ChevronRight";
import { Close as CloseIcon } from "@remo-co/ui-core/src/icons/Close";
import { Done as DoneIcon } from "@remo-co/ui-core/src/icons/Done";
import { Search } from "@remo-co/ui-core/src/icons/Search";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import logger from "logging/logger";
import { selectCurrentEventId } from "modules/event/selectors";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import { useAppDispatch } from "store/hooks";
import { CURRENT_COMPANY_ID } from "../../constants";
import Picture from "../../assets/company.svg";
import { ICompany, IUserCompany } from "../../types";
import { styles } from "./styles";

interface Props {
  handleClick?: (companyId?: string) => void;
  showTitle?: boolean;
  onClose?: () => void;
  embedded?: boolean;
}

const CompaniesList = ({
  handleClick,
  showTitle,
  onClose,
  embedded,
}: Props) => {
  const { track } = useContext(TRACKING_CONTEXT);
  const { t } = useI18n(["menu", "common"]);
  const company = useSelector(selectMyCompany);
  const currentEventId = useSelector(selectCurrentEventId);
  const currentCompanyId = useSelector(selectCompanyId);
  const userCompanies = useSelector(selectCompanies);
  const dispatch = useAppDispatch();

  const { role } = useSelector(selectAuth);
  const [query, setQuery] = useState("");
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [matchedCompanies, setMatchedCompanies] = useState<IUserCompany[]>([]);

  // Need to show search option if more then 5 companies
  useEffect(() => {
    setMatchedCompanies(userCompanies);
    setShowSearch(userCompanies?.length > 5);
  }, [userCompanies]);

  const switchCompany = useCallback(
    (companyId: string | undefined) => {
      if (!companyId) {
        logger.warn("[switchCompany] invalid companyid");

        return;
      }

      if (currentCompanyId) {
        track(
          Events.SIGNED_OUT_B2B,
          {},
          {
            groupId: currentCompanyId,
          },
        );
      }

      const companyInfo = userCompanies.filter(
        ({ company: thisCompany }) => thisCompany.id === companyId,
      );

      if (companyInfo?.length) {
        localStorage.setItem(CURRENT_COMPANY_ID, companyId);
        window.location.reload();
      }
    },
    [userCompanies, currentCompanyId, track],
  );

  const handleSwitch = useCallback(
    (companyId?: string) => {
      if (handleClick) {
        handleClick(companyId);

        return;
      }

      switchCompany(companyId);
    },
    [switchCompany, handleClick],
  );

  const selectCompany = useCallback(
    (companyId?: string) => {
      if (currentEventId) {
        dispatch(
          addDialogNotification({
            message: t("common:company.switch.confirmation"),
            onConfirm: () => handleSwitch(companyId),
            confirmText: t("common:button.confirm"),
            dismissText: t("common:button.cancel"),
            title: t("user.menu.change.company"),
            lightDialog: true,
          }),
        );
      } else {
        handleSwitch(companyId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEventId, handleSwitch],
  );

  const handleCompanySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchQuery = e.target.value;

    setQuery(searchQuery);

    const matched = userCompanies.filter((userInfo) =>
      userInfo?.company?.name?.includes(searchQuery),
    );

    setMatchedCompanies(matched);
  };

  const handleCompanyItemClick = (userCompanyInfo: IUserCompany) => {
    selectCompany(userCompanyInfo?.company?.id);
  };

  const showCompanyInfo = useCallback(
    (companyInfo: ICompany, userRole: string) => {
      const active = companyInfo?.id === company?.id;
      const messageKey = `menu:${
        active ? "logged" : "continue"
      }.as.${userRole}`;

      return (
        <Box sx={styles.item}>
          <Box sx={styles.info}>
            {companyInfo?.logoURL ? (
              <Box sx={styles.img}>
                <Box
                  component="img"
                  src={companyInfo?.logoURL}
                  sx={styles.logo}
                  alt="Company Logo"
                />
              </Box>
            ) : (
              <Box sx={styles.imgIcon}>
                <img src={Picture} alt="Company Logo Placeholder" />
              </Box>
            )}

            <div>
              <Typography sx={styles.name}>
                {companyInfo?.name !== ""
                  ? companyInfo?.name
                  : t("menu:unnamed.company")}
              </Typography>
              <Typography sx={styles.role}>{t(messageKey)}</Typography>
            </div>
          </Box>
          {active ? (
            <DoneIcon sx={styles.activeIcon} />
          ) : (
            <ChevronRight sx={styles.icon} />
          )}
        </Box>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [company],
  );

  return (
    <Box sx={embedded ? styles.embeddedContainer : styles.container}>
      {showTitle && (
        <Box sx={styles.desc}>
          <Typography sx={styles.title}>{t("menu:select.company")}</Typography>
          <CloseIcon
            sx={styles.closeIcon}
            onClick={onClose}
            data-testid="switch-companies-close-btn"
          />
        </Box>
      )}
      {showSearch && (
        <Box sx={styles.search}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              style: {
                height: "40px",
              },
            }}
            placeholder={t("menu:search.company")}
            onChange={handleCompanySearch}
            value={query}
            variant="outlined"
            style={{
              width: "100%",
            }}
          />
        </Box>
      )}

      <ScrollableView>
        <Box
          sx={showSearch ? styles.fixedHeightClContainer : styles.clContainer}
        >
          {!query && company && (
            <Box sx={styles.companyItem}>
              {showCompanyInfo(company, role || "")}
            </Box>
          )}
          {matchedCompanies.map((userCompanyInfo) => {
            if (!query && userCompanyInfo?.company?.id === company?.id) {
              return;
            }

            // eslint-disable-next-line consistent-return
            return (
              <ListItem
                key={userCompanyInfo.company.id}
                button
                sx={styles.companyItem}
                onClick={() => handleCompanyItemClick(userCompanyInfo)}
              >
                {showCompanyInfo(
                  userCompanyInfo?.company,
                  userCompanyInfo?.role,
                )}
              </ListItem>
            );
          })}
          {query && !matchedCompanies?.length && (
            <div className="nomatch">
              <Typography>{t("menu:no.result")}</Typography>
            </div>
          )}
        </Box>
      </ScrollableView>
    </Box>
  );
};

export default CompaniesList;
